import React, { useState } from 'react';
import { Close } from '@mui/icons-material';
import comms from '../../comms';

// Schedule ACH Drop dialog (docs/DriverRequirement/08_driverapp_cashiering_handoff.md):
// amount is constrained to (0, available], submits { driverId, amount } to
// /API/Driver/ScheduleDrop, disables resubmission while in flight, and surfaces
// the server's error message verbatim for the missing-ACH-setup / insufficient
// balance cases rather than guessing at friendlier copy.
function ScheduleDropDialog({ open, driverId, available, onClose, onScheduled }) {
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  if (!open) return null;

  const handleSubmit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setFormError('Enter an amount greater than $0.00');
      return;
    }
    if (available != null && amt > available) {
      setFormError(`Amount cannot exceed your available balance ($${Number(available).toFixed(2)})`);
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await comms.scheduleAchDrop(driverId, amt);
      if (!res || res.ok === false) {
        setFormError(res?.error || 'Unable to schedule drop. Please try again.');
        return;
      }
      setAmount('');
      onScheduled(res);
    } catch (e) {
      setFormError(e?.response?.data?.error || e?.message || 'Unable to schedule drop. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-md rounded-t-2xl bg-white p-4 shadow-lg sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Schedule ACH Drop</h3>
          <button
            onClick={onClose}
            aria-label="Cancel"
            disabled={submitting}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            <Close style={{ fontSize: 20 }} />
          </button>
        </div>

        {available != null && (
          <div className="mb-3 text-xs text-gray-500">
            Available balance: <span className="font-semibold text-gray-800">${Number(available).toFixed(2)}</span>
          </div>
        )}

        <label className="mb-1 block text-xs font-medium text-gray-500">Amount</label>
        <div className="flex items-center rounded-xl border border-gray-300 px-3 focus-within:border-blue-600">
          <span className="text-lg font-semibold text-gray-400">$</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            max={available ?? undefined}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            autoFocus
            disabled={submitting}
            className="w-full bg-transparent px-2 py-3 text-lg font-semibold text-gray-900 outline-none disabled:opacity-50"
          />
        </div>

        {formError && (
          <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{formError}</div>
        )}

        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting ? 'Scheduling…' : 'Schedule Drop'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScheduleDropDialog;
